import { Configuration, FireplaceModel } from '../types';
import { getFusionPrices, getFusionCornerPrices, isFusionCornerAvailableForModel } from '../data/fusionPrices';
import { nemoRequiresColorSelection, nemoIsRT08GX } from '../data/controllerData';

// Helper function to check if model is a NEMO model
export const isNemoModel = (modelId: string | null): boolean => {
  if (!modelId) return false;
  return modelId.toLowerCase().startsWith('nemo-');
};

// Generate NEMO thermal protection options
export function generateNemoThermalProtectionOptions(model: FireplaceModel | null): OptionGroup | null {
  if (!isNemoModel(model?.id || null)) return null;

  return {
    id: 'nemoThermalProtection',
    title: 'ZABEZPIECZENIE TERMICZNE CHŁODNICY',
    selectedValue: 'standard',
    required: false,
    options: [
      {
        value: 'thermal-protection-option',
        label: 'Zabezpieczenie termiczne chłodnicy',
        description: 'Czy chcesz dodać zabezpieczenie termiczne chłodnicy?',
        price: 420
      }
    ]
  };
}

// Mapa wymiarów FUSION dla modeli Dragon (sz/w/g [mm]) - na podstawie tabeli UNICO FUSION
const FUSION_DIMENSIONS: { [modelId: string]: { standard: string, lift: string } } = {
  // D2 series
  'dragon-2-slim': { standard: '860/1960/570', lift: '950/1960/610' },
  'dragon-2': { standard: '860/1960/570', lift: '950/1960/610' },
  'dragon-2-xl': { standard: '860/1960/570', lift: '950/1960/610' },
  
  // D2B series (narożne) 
  'dragon-2b-slim': { standard: '860/1960/570', lift: '860/1960/610' },
  'dragon-2b': { standard: '860/1960/570', lift: '860/1960/610' },
  'dragon-2b-xl': { standard: '860/1960/570', lift: '860/1960/610' },
  
  // D4 series
  'dragon-4-slim': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-4': { standard: '950/1960/610', lift: '1100/1960/770' },
  'dragon-4-xl-slim': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-4-xl': { standard: '950/1960/610', lift: '1100/1960/770' },
  
  // D4B series (narożne)
  'dragon-4b-slim': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-4b': { standard: '950/1960/610', lift: '1100/1960/770' },
  'dragon-4b-xl-slim': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-4b-xl': { standard: '950/1960/610', lift: '1100/1960/770' },
  
  // D6 series
  'dragon-6': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-6-xl': { standard: '950/1950/570', lift: '1100/1950/610' },
  'dragon-6-xxl': { standard: '950/1960/570', lift: '1100/1960/610' },
  
  // D8 series
  'dragon-8': { standard: '740/2001/610', lift: '740/2001/610' }, // brak lift w tabeli - używam standard
  'dragon-8-xxl': { standard: '740/2180/610', lift: '740/2180/610' }, // brak lift w tabeli - używam standard  
  'dragon-8-mini': { standard: '740/2001/570', lift: '740/2001/610' }, // D8 Slim/5 w tabeli
  'dragon-8-slim': { standard: '740/2001/570', lift: '740/2001/610' },
  
  // D8B series (narożne)
  'dragon-8b': { standard: '740/2001/570', lift: '740/2001/610' },
  'dragon-8b-xxl': { standard: '740/2180/570', lift: '740/2180/570' }, // brak lift w tabeli
  
  // D9 series - domyślne (brak w tabeli)
  'dragon-9': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-9-xl': { standard: '950/1960/570', lift: '1100/1960/610' },
  'dragon-9-corner': { standard: '950/1960/570', lift: '1100/1960/610' },
  
  // NEMO series - na podstawie tabeli NEMO FUSION
  // N2 series
  'nemo-2': { standard: '950/1960/570', lift: '950/1960/610' },
  'nemo-2-xl': { standard: '950/1960/570', lift: '950/1960/610' },
  
  // N2B series (narożne)
  'nemo-2b': { standard: '950/1960/570', lift: '950/1960/610' },
  'nemo-2b-xl': { standard: '950/1960/570', lift: '950/1960/610' },
  
  // N4 series
  'nemo-4': { standard: '1100/1960/770', lift: '1100/1960/770' },
  'nemo-4-slim': { standard: '1100/1960/570', lift: '1100/1960/770' }, // N4B Slim w tabeli
  
  // N4B series (narożne)
  'nemo-4b': { standard: '1100/1960/770', lift: '1100/1960/770' },
  'nemo-4b-slim': { standard: '1100/1960/570', lift: '1100/1960/770' },
  
  // N6 series  
  'nemo-6': { standard: '1100/1960/770', lift: '1100/1960/770' }, // używam N4 jako domyślne
  'nemo-6-xl': { standard: '1100/1950/570', lift: '1100/1950/610' },
  'nemo-6b': { standard: '1100/1960/770', lift: '1100/1960/770' }, // używam N4B jako domyślne
  
  // N8 series - domyślne (brak w tabeli)
  'nemo-8': { standard: '1100/1960/770', lift: '1100/1960/770' },
  'nemo-8b': { standard: '1100/1960/770', lift: '1100/1960/770' },
  
  // N9 series - domyślne (brak w tabeli)  
  'nemo-9': { standard: '1100/1960/770', lift: '1100/1960/770' }
}

// Funkcja pobierająca wymiary FUSION dla modelu
function getFusionDimensions(model: FireplaceModel | null, variant: 'standard' | 'lift' = 'standard'): string {
  if (!model?.id) return '950/1960/570' // domyślne wymiary
  
  const dimensions = FUSION_DIMENSIONS[model.id]
  if (!dimensions) return '950/1960/570' // domyślne wymiary
  
  return dimensions[variant] || dimensions.standard || '950/1960/570'
}

// Mapa wymiarów FUSION CORNER dla modeli (sz/w/g [mm]) - na podstawie tabeli UNICO FUSION CORNER
const FUSION_CORNER_DIMENSIONS: { [modelId: string]: { standard: string, lift: string } } = {
  // DRAGON CORNER MODELS
  // D2B series (narożne)
  'dragon-2b': { standard: '860/1960/570', lift: '860/1960/610' },
  'dragon-2b-slim': { standard: '860/1960/570', lift: '860/1960/610' },
  
  // D4B series (narożne) 
  'dragon-4b-slim': { standard: '950/1960/570', lift: '950/1960/610' },
  
  // D8B series (narożne)
  'dragon-8b': { standard: '740/2001/570', lift: '740/2001/610' },
  'dragon-8b-xxl': { standard: '740/2180/570', lift: '740/2180/570' }, // brak lift w tabeli
  
  // NEMO CORNER MODELS
  // N2B series (narożne)
  'nemo-2b': { standard: '950/1960/570', lift: '950/1960/610' },
  
  // N4B series (narożne)
  'nemo-4b': { standard: '1100/1960/770', lift: '1100/1960/770' },
  'nemo-4b-slim': { standard: '1100/1960/570', lift: '1100/1960/770' }
}

// Funkcja pobierająca wymiary FUSION CORNER dla modelu
function getFusionCornerDimensions(model: FireplaceModel | null, variant: 'standard' | 'lift' = 'standard'): string {
  if (!model?.id) return '950/1960/570' // domyślne wymiary
  
  const dimensions = FUSION_CORNER_DIMENSIONS[model.id]
  if (!dimensions) return '950/1960/570' // domyślne wymiary
  
  return dimensions[variant] || dimensions.standard || '950/1960/570'
}

export interface OptionItem {
  value: string;
  label: string;
  description?: string;
  price?: number;
  disabled?: boolean;
  colorPreview?: string;
  icon?: string; // Path to icon image for desktop display
  isCheckbox?: boolean;
  hasCounter?: boolean;
  counterMin?: number;
  counterMax?: number;
  counterDefault?: number;
  exclusiveWith?: string; // Pole do wykluczania opcji
}

export interface OptionGroup {
  id: string;
  title: string;
  subtitle?: string; // Optional subtitle displayed below the title
  options: OptionItem[];
  selectedValue?: string;
  disabled?: boolean;
  visible?: boolean;
  required?: boolean; // Mark as required field
  showSmallGlassOption?: boolean; // For corner models - show checkbox for small glass
}

// Check if model has specific type flag
export function hasTypeFlag(model: FireplaceModel | null, flag: string): boolean {
  if (!model || !model.type) return false;
  
  return model.type.includes(flag);
}

// Check if FUSION is available for this model
export function isFusionAvailable(model: FireplaceModel | null): boolean {
  return !hasTypeFlag(model, 'nofusion');
}

// Check if FUSION CORNER is available for this model
export function isFusionCornerAvailable(model: FireplaceModel | null, variant: 'standard' | 'lift' = 'standard'): boolean {
  if (!model?.id || hasTypeFlag(model, 'nofusioncorner')) return false;
  
  // Check if model is corner type
  const isCornerModel = model.glassType === 'narozna' || model.glassType === 'corner';
  if (!isCornerModel) return false;
  
  return isFusionCornerAvailableForModel(model.id, variant);
}

// Check if AKU+ is available for this model  
export function isAkuPlusAvailable(model: FireplaceModel | null): boolean {
  if (!model || !model.type?.includes('powietrzny-aku')) return false;
  
  // Check if model has akuPlus options in extraPrices
  return !!(model.extraPrices?.akuPlus);
}

// Check if model is vis-a-vis type
export function isVisAVisModel(model: FireplaceModel | null): boolean {
  return model?.glassType === 'vis-a-vis';
}

// Check if LIFT is available for this model
export function isLiftAvailable(model: FireplaceModel | null): boolean {
  if (!model?.basePrices?.lift) return false;
  return Object.values(model.basePrices.lift).some(price => price !== null);
}

// Check if model is AKU and vis-a-vis (used to grey-out LIFT option)
export function isAkuVisAVis(model: FireplaceModel | null): boolean {
  return !!(model && model.type?.includes('powietrzny-aku') && model.glassType === 'vis-a-vis');
}

// Check if STANDARD is available for this model
export function isStandardAvailable(model: FireplaceModel | null): boolean {
  if (!model?.basePrices?.standard) return false;
  return Object.values(model.basePrices.standard).some(price => price !== null);
}

// Generate variant options
export function generateVariantOptions(model: FireplaceModel | null): OptionGroup {
  const liftAvailable = isLiftAvailable(model);
  const standardAvailable = isStandardAvailable(model);
  // For AKU vis-a-vis models we want to show LIFT but disabled (greyed-out), and auto-select standard
  const liftDisabled = isAkuVisAVis(model) ? true : !liftAvailable;
  const selected = standardAvailable ? 'standard' : (liftAvailable ? 'lift' : '');
  
  // Calculate LIFT price dynamically
  const liftPrice = model?.basePrices?.lift?.single && model?.basePrices?.standard?.single 
    ? model.basePrices.lift.single - model.basePrices.standard.single 
    : 0;
  
  const liftDescription = liftDisabled ? 'Niedostępny dla tego modelu' : (liftAvailable ? 'System PowerLift' : 'Niedostępny dla tego modelu');

  return {
    id: 'variant',
    title: 'WARIANT OTWIERANIA',
    selectedValue: selected,
    options: [
      {
        value: 'standard',
        label: 'Standard - otwieranie na bok',
        description: standardAvailable ? 'Klasyczne drzwi otwierane na bok' : 'Niedostępny dla tego modelu',
        disabled: !standardAvailable
      },
      {
        value: 'lift',
        label: 'LIFT (gilotyna) - podnoszenie do góry',
        description: liftDescription,
        disabled: liftDisabled,
        ...(liftAvailable && !liftDisabled && liftPrice > 0 ? { price: liftPrice } : {})
      }
    ]
  };
}

// Generate glass type options based on model type
export function generateGlassOptions(model: FireplaceModel | null, config?: Configuration): OptionGroup {
  if (!model) {
    return {
      id: 'glass',
      title: 'TYP SZYBY',
      selectedValue: '',
      options: []
    };
  }

  if (model.name === 'Dragon 8R') {
    return {
      id: 'glass',
      title: 'TYP SZYBY',
      selectedValue: '',
      options: [
        {
          value: 'single',
          label: 'Szyba półokrągła R330',
          description: 'Specjalna szyba półokrągła'
        }
      ]
    };
  }

  if (model.glassType === 'narozna') {
    const options: OptionItem[] = [];
    
    // Get available glass types from model's basePrices (check both standard and lift)
    const glassTypes = new Set<string>();
    
    if (model.basePrices?.standard) {
      Object.keys(model.basePrices.standard).forEach(glassType => {
        glassTypes.add(glassType);
      });
    }
    
    if (model.basePrices?.lift) {
      Object.keys(model.basePrices.lift).forEach(glassType => {
        glassTypes.add(glassType);
      });
    }
    
    // Determine which variant to use for pricing
    const variant = config?.variant === 'lift' ? 'lift' : 'standard';
    const variantPrices = model.basePrices?.[variant] || model.basePrices?.standard || {};
    
    // Find the cheapest glass option as base price
    const basePrice = Math.min(...Array.from(glassTypes).map(glassType => variantPrices[glassType] || 0));
    
    glassTypes.forEach(glassType => {
      const label = model.labels?.glass?.[glassType] || glassType;
      const glassPrice = variantPrices[glassType] || 0;
      const priceDiff = glassPrice - basePrice;
      
      // Add appropriate description and icon based on glass type
      let description = '';
      let icon = '';
      
      if (glassType.includes('connected')) {
        if (glassType.includes('single')) {
          description = 'Szyba narożna gięta pojedyncza';
          icon = 'images/OptionIcons/szyba gięta x1.png';
        } else if (glassType.includes('double')) {
          description = 'Szyba narożna gięta podwójna';
          icon = 'images/OptionIcons/Szyba gięta x2.png';
        } else {
          description = 'Szyba narożna gięta';
          icon = 'images/OptionIcons/szyba gięta x1.png';
        }
      } else if (glassType.includes('single')) {
        description = 'Szyba narożna dzielona pojedyncza';
        icon = 'images/OptionIcons/Szyba łączona x1.png';
      } else if (glassType.includes('double')) {
        description = 'Szyba zewnętrzna dzielona, wewnętrzna gięta';
        icon = 'images/OptionIcons/Szyba dzielona x2.png';
      }
      
      options.push({
        value: glassType,
        label,
        ...(description ? { description } : {}),
        ...(icon ? { icon } : {}),
        ...(priceDiff > 0 ? { price: priceDiff } : {})
      });
    });
    
    return {
      id: 'glass',
      title: 'TYP SZYBY',
      selectedValue: options.length === 1 ? options[0].value : '', // Auto-select if only one option
      options
    };
  }

  if (model.glassType === 'vis-a-vis') {
    // Calculate price differences for vis-a-vis models - use appropriate variant
    const variant = config?.variant === 'lift' ? 'lift' : 'standard';
    const variantPrices = model.basePrices?.[variant] || model.basePrices?.standard || {};
    const basePrice = variantPrices.single || 0;
    
    return {
      id: 'glass',
      title: 'TYP SZYBY',
      subtitle: 'Wybór dotyczy dwóch stron wkładu',
      selectedValue: '',
      options: [
        {
          value: 'single',
          label: 'Pojedyncza x1 (S2)',
          description: 'Standardowa szyba pojedyncza'
          // No price field - base price, no extra cost
        },
        {
          value: 'double',
          label: 'Podwójna x2 (S3)',
          description: 'Lepsza izolacja termiczna',
          price: (variantPrices.double || 0) - basePrice
        },
        {
          value: 'triple',
          label: 'Potrójna x3 (S11)',
          description: 'Maksymalna izolacja',
          price: (variantPrices.triple || 0) - basePrice
        }
      ]
    };
  }

  // For other models (prosta, etc.) - calculate price differences using appropriate variant
  const variant = config?.variant === 'lift' ? 'lift' : 'standard';
  const variantPrices = model.basePrices?.[variant] || model.basePrices?.standard || {};
  const basePrice = variantPrices.single || 0;

  // Build options array based on what's available in basePrices and labels
  const options: OptionItem[] = [];
  const glassLabels = model.labels?.glass || {};
  
  // Check which glass types are available
  if (variantPrices.single !== undefined && glassLabels.single) {
    options.push({
      value: 'single',
      label: 'Pojedyncza x1 (S2)',
      description: 'Standardowa szyba pojedyncza'
    });
  }
  
  if (variantPrices.double !== undefined && glassLabels.double) {
    options.push({
      value: 'double',
      label: 'Podwójna x2 (S3)',
      description: 'Lepsza izolacja termiczna',
      price: (variantPrices.double || 0) - basePrice
    });
  }
  
  if (variantPrices.triple !== undefined && glassLabels.triple) {
    options.push({
      value: 'triple',
      label: 'Potrójna x3 (S11)',
      description: 'Maksymalna izolacja',
      price: (variantPrices.triple || 0) - basePrice
    });
  }

  return {
    id: 'glass',
    title: 'TYP SZYBY',
    selectedValue: options.length === 1 ? options[0].value : '',
    options
  };
}

// Generate handle side options
export function generateHandleSideOptions(config: Configuration, model?: any): OptionGroup {
  const isStandardVariant = config.variant === 'standard';
  const isCornerModel = model?.glassType === 'narozna';
  const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
  const hasSideGlassSelection = config.sideGlass;
  
  // Hide handle side selection for standard corner models when side glass is selected
  const shouldHideForCorner = isStandardVariant && isCornerModel && isDragonOrNemo && hasSideGlassSelection;
  
  const options = [
    {
      value: 'right',
      label: 'Klamka po stronie prawej',
      description: 'Standardowe położenie klamki'
    },
    {
      value: 'left',
      label: 'Klamka po stronie lewej',
      description: 'Lustrzane odbicie'
    }
  ];
  
  return {
    id: 'handleSide',
    title: 'STRONA KLAMKI',
    selectedValue: '',
    visible: isStandardVariant && !shouldHideForCorner,
    showSmallGlassOption: false, // Completely disabled - no longer needed
    options
  };
}

// Generate side glass options for LIFT corner models
export function generateSideGlassOptions(config: Configuration, model?: any): OptionGroup {
  const isLiftVariant = config.variant === 'lift';
  const isStandardVariant = config.variant === 'standard';
  const isCornerModel = model?.glassType === 'narozna';
  const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
  const isDragon2C = model?.name === 'DRAGON 2C';
  
  // For standard corner models, include handle position in description
  const options = [
    {
      value: 'right',
      label: 'Prawa',
      description: isStandardVariant
        ? 'Boczna szyba po prawej stronie wkładu (klamka po lewej)'
        : 'Boczna szyba po prawej stronie wkładu'
    },
    {
      value: 'left',
      label: 'Lewa',
      description: isStandardVariant
        ? 'Boczna szyba po lewej stronie wkładu (klamka po prawej)'
        : 'Boczna szyba po lewej stronie wkładu'
    }
  ];
  
  return {
    id: 'sideGlass',
    title: 'STRONA WKŁADU',
    selectedValue: '',
    visible: (isLiftVariant || isStandardVariant) && isCornerModel && isDragonOrNemo && !isDragon2C,
    options
  };
}

// Generate ceramic options
export function generateCeramicOptions(): OptionGroup {
  return {
    id: 'ceramic',
    title: 'KOLOR CERAMIKI (WYŁOŻENIE WNĘTRZA)',
    selectedValue: '',
    options: [
      {
        value: 'refrabox-light',
        label: 'Jasna Refrabox (W2)',
        description: 'Ceramika jasna Refrabox',
        colorPreview: '#f5f1e8'
      },
      {
        value: 'cerabox-graphite',
        label: 'Grafitowa Cerabox (W5)',
        description: 'Ceramika grafitowa Cerabox',
        colorPreview: '#4a4a4a'
      }
    ]
  };
}

// Generate handle options based on variant
export function generateHandleOptions(config: Configuration): OptionGroup {
  const isLift = config.variant === 'lift';
  
  const baseOptions = [
    {
      value: 'classic-black',
      label: isLift ? 'Classic czarna (KL1)' : 'Classic czarna (K1)',
      description: 'Standardowa klamka czarna',
      colorPreview: '#333'
    },
    {
      value: 'classic-silver',
      label: isLift ? 'Classic srebrna (KL2)' : 'Classic srebrna (K2)',
      description: 'Standardowa klamka srebrna',
      colorPreview: '#c0c0c0'
    }
  ];

  // Add design options only for standard variant
  if (!isLift) {
    baseOptions.push(
      {
        value: 'design-black',
        label: 'Design czarna (K7)',
        description: 'Designerska klamka czarna',
        colorPreview: '#1a1a1a'
      },
      {
        value: 'design-black-silver',
        label: 'Design czarno-srebrna (K5)',
        description: 'Designerska klamka dwukolorowa',
        colorPreview: 'linear-gradient(45deg,#333,#c0c0c0)'
      }
    );
  }

  return {
    id: 'handle',
    title: 'KOLOR KLAMKI',
    selectedValue: '',
    options: baseOptions
  };
}

// Generate bezel options based on variant and model
export function generateBezelOptions(config: Configuration, model: FireplaceModel | null): OptionGroup {
  const isLift = config.variant === 'lift';
  
  const baseOptions: OptionItem[] = [
    {
      value: 'none',
      label: 'Bez blendy',
      description: 'Standardowe wykończenie'
    }
  ];

  // Special handling for Dragon 8R
  if (model?.name === 'Dragon 8R') {
    baseOptions.push({
      value: 'flush-30',
      label: 'Licująca 30mm (R30)',
      description: 'Wywinięcie 9mm',
      price: model?.extraPrices?.bezel?.standard?.['flush-30'] || 0
    });
    
    return {
      id: 'bezel',
      title: 'BLENDA WYKOŃCZENIOWA',
      selectedValue: '',
      options: baseOptions
    };
  }

  // Special handling for Dragon 2C - only "bez blendy" and "LIFT 15mm"
  if (model?.name === 'DRAGON 2C') {
    if (isLift) {
      baseOptions.push({
        value: 'recessed-lift-15',
        label: 'LIFT 15mm (RL15)',
        description: 'Bez wywinięcia',
        price: model?.extraPrices?.bezel?.lift?.['recessed-lift-15'] || 0
      });
    }
    
    return {
      id: 'bezel',
      title: 'BLENDA WYKOŃCZENIOWA',
      selectedValue: '',
      options: baseOptions
    };
  }

  // Special handling for Dragon 8 MINI - use only defined bezels from model
  if (model?.name === 'DRAGON 8 MINI') {
    if (model?.extraPrices?.bezel?.standard) {
      Object.entries(model.extraPrices.bezel.standard).forEach(([key, price]) => {
        if (key === 'none') return; // Skip none, already added
        const label = model.labels?.bezel?.[key] || key;
        const descriptions: { [key: string]: string } = {
          'flush-15': 'Bez wywinięcia',
          'flush-30': 'Wywinięcie 9mm',
          'recessed-15-45': 'Kąt 45°'
        };
        baseOptions.push({
          value: key,
          label,
          description: descriptions[key] || '',
          price: typeof price === 'number' ? price : 0
        });
      });
    }
    
    return {
      id: 'bezel',
      title: 'BLENDA WYKOŃCZENIOWA',
      selectedValue: '',
      options: baseOptions
    };
  }

  if (isLift) {
    // Add LIFT bezel options only if they exist in model's pricing
    if (model?.extraPrices?.bezel?.lift?.['recessed-lift-15'] !== undefined) {
      baseOptions.push({
        value: 'recessed-lift-15',
        label: 'LIFT 15mm (RL15)',
        description: 'Bez wywinięcia',
        price: model.extraPrices.bezel.lift['recessed-lift-15']
      });
    }
    if (model?.extraPrices?.bezel?.lift?.['recessed-lift-30'] !== undefined) {
      baseOptions.push({
        value: 'recessed-lift-30',
        label: 'LIFT 30mm (RL30)',
        description: 'Wywinięcie 9mm',
        price: model.extraPrices.bezel.lift['recessed-lift-30']
      });
    }
    if (model?.extraPrices?.bezel?.lift?.['recessed-lift-5'] !== undefined) {
      baseOptions.push({
        value: 'recessed-lift-5',
        label: 'LIFT 5mm (RL05)',
        description: 'Bez wywinięcia',
        price: model.extraPrices.bezel.lift['recessed-lift-5']
      });
    }
  } else {
    // Use labels from model data if available, otherwise use defaults
    const bezelKeys = ['flush-15', 'flush-30', 'recessed-15', 'recessed-30', 'recessed-15-45'];
    const descriptions: { [key: string]: string } = {
      'flush-15': 'Bez wywinięcia',
      'flush-30': 'Bez wywinięcia',
      'recessed-15': 'Bez wywinięcia',
      'recessed-30': 'Wywinięcie 9mm',
      'recessed-15-45': 'Kąt 45°'
    };
    
    bezelKeys.forEach(key => {
      const price = model?.extraPrices?.bezel?.standard?.[key] || 0;
      const label = model?.labels?.bezel?.[key] || key;
      baseOptions.push({
        value: key,
        label,
        description: descriptions[key] || '',
        price: typeof price === 'number' ? price : 0
      });
    });
  }

  return {
    id: 'bezel',
    title: 'BLENDA WYKOŃCZENIOWA',
    selectedValue: '',
    options: baseOptions
  };
}

// Generate AKU+ option if available
export function generateAkuPlusOptions(model: FireplaceModel | null): OptionGroup | null {
  if (!isAkuPlusAvailable(model)) return null;
  
  return {
    id: 'akuPlus',
    title: 'AKU+',
    selectedValue: 'none',
    options: [
      {
        value: 'none',
        label: 'Standardowa wersja',
        description: 'Bez AKU+'
      },
      {
        value: 'aku-plus',
        label: 'Wybieram wersję AKU+',
        description: 'z dodatkową izolowaną komorą nad komorą spalania',
        price: 860
      }
    ]
  };
}

// Check if AKU BOX is available for the model
export function isAkuBoxAvailable(model: FireplaceModel | null): boolean {
  if (!model || !model.id) return false;
  
  // AKU BOX available for specific Dragon models
  const akuBoxModels = [
    'dragon-2', 'dragon-2-xl', 'dragon-2b', 'dragon-2b-xl',
    'dragon-4-slim', 'dragon-4b-slim',
    'dragon-4', 'dragon-4b',
    'dragon-6', 'dragon-6-xl',
    'dragon-9'
  ];
  
  return akuBoxModels.includes(model.id);
}

// Get AKU BOX price for the model
export function getAkuBoxPrice(model: FireplaceModel | null): number {
  if (!model || !model.id) return 0;
  
  const akuBoxPrices: { [key: string]: number } = {
    'dragon-2': 2980,
    'dragon-2-xl': 2980,
    'dragon-2b': 2980,
    'dragon-2b-xl': 2980,
    'dragon-4-slim': 3500,
    'dragon-4b-slim': 3500,
    'dragon-4': 4540,
    'dragon-4b': 4540,
    'dragon-6': 3760,
    'dragon-6-xl': 3760,
    'dragon-9': 4760
  };
  
  return akuBoxPrices[model.id] || 0;
}

// Check if CMA modules are available (only for Dragon AKU models - proste i narożne, nie vis-a-vis)
export function isCMAAvailable(model: FireplaceModel | null): boolean {
  if (!model || !model.name) return false;
  
  // CMA available only for Dragon models with AKU in the name
  const hasAku = model.name.includes('AKU');
  
  // For vis-a-vis, only specific DUO models: 4 DUO, 8 DUO, 9 DUO
  if (model.glassType === 'vis-a-vis') {
    const isDuoModel = model.id === 'dragon-aku-4-duo' || 
                       model.id === 'dragon-aku-8-duo' || 
                       model.id === 'dragon-aku-9-duo';
    return hasAku && isDuoModel;
  }
  
  // For proste and narozne - all AKU models
  return hasAku;
}

// Generate accumulation materials options
export function generateAccumulationMaterialsOptions(model: FireplaceModel | null, config?: Configuration): OptionGroup | null {
  if (!model) return null;
  
  const modelId = (model.id || '').toLowerCase();
  const isNemoModel = modelId.startsWith('nemo') || model.name.toLowerCase().includes('nemo');
  if (isNemoModel) {
    return null;
  }

  const options = [];
  const isDragonAku = isCMAAvailable(model);
  
  // Check if thermal modules are selected (for mutual exclusion)
  const thermalModulesSelected = config?.thermalModules === 'thermal' && (config?.thermalModulesCount || 0) > 0;
  
  // AKU BOX option - only for specific models (not AKU models)
  if (isAkuBoxAvailable(model) && !isDragonAku) {
    const akuBoxPrice = getAkuBoxPrice(model);
    options.push({
      value: 'aku-box',
      label: 'AKU BOX',
      description: `Wymiennik AKU BOX`,
      price: akuBoxPrice,
      isCheckbox: true,
      disabled: thermalModulesSelected // Disable when thermal modules are selected
    });
  }
  
  // CMA modules - only for Dragon AKU models (proste i narożne)
  if (isDragonAku) {
    options.push({
      value: 'cma-n',
      label: 'CMA N',
      description: 'Moduł akumulacyjny 333 kg (112x56x35 mm)',
      price: 2880,
      isCheckbox: true
    });
    
    options.push({
      value: 'cma-s',
      label: 'CMA S',
      description: 'Moduł akumulacyjny 338 kg (119x56x28 mm)',
      price: 3030,
      isCheckbox: true
    });
  } else {
    // EASY 700 option - available for non-AKU, non-Nemo models only
    options.push({
      value: 'easy-700',
      label: 'EASY 700',
      description: 'Wymiennik stalowo-szamotowy',
      price: 2210,
      isCheckbox: true
    });
  }
  
  // Only show section if there are options
  if (options.length === 0) return null;
  
  return {
    id: 'accumulationMaterials',
    title: 'MATERIAŁY AKUMULACYJNE',
    selectedValue: 'none',
    options
  };
}

// Check if thermal modules are available for the model
export function isThermalModulesAvailable(model: FireplaceModel | null): boolean {
  if (!model || !model.extraPrices?.thermalModules) return false;
  
  return Object.keys(model.extraPrices.thermalModules).length > 1; // More than just 'none'
}

// Get thermal modules options and prices
export function getThermalModulesOptions(model: FireplaceModel | null): { value: string, label: string, price: number }[] {
  if (!model?.extraPrices?.thermalModules || !model?.labels?.thermalModules) return [];
  
  const options = [];
  const prices = model.extraPrices.thermalModules;
  const labels = model.labels.thermalModules;
  
  for (const [value, price] of Object.entries(prices)) {
    if (value !== 'none') {
      options.push({
        value,
        label: labels[value] || value,
        price
      });
    }
  }
  
  return options;
}

// Generate thermal modules options
export function generateThermalModulesOptions(model: FireplaceModel | null, config?: Configuration): OptionGroup | null {
  if (!isThermalModulesAvailable(model)) return null;
  
  const thermalOptions = getThermalModulesOptions(model);
  if (thermalOptions.length === 0) return null;
  
  // Check if AKU BOX is selected (for mutual exclusion)
  const akuBoxSelected = config?.akuBox === 'aku-box';
  
  // Get the price and label for thermal module from model data
  const singleModulePrice = thermalOptions[0]?.price || 260;
  let moduleLabel = thermalOptions[0]?.label || 'MODUŁY termoakumulacyjny';
  
  // Add "każdy" to weight in parentheses
  moduleLabel = moduleLabel.replace(/\((\d+,?\d*)\s*kg\)/, '($1 kg każdy)');
  
  // Determine max modules based on model type
  const modelId = model?.id || '';
  let maxModules = 7;
  let maxNote = 'maksymalnie 7 sztuk';
  
  // Dragon 2 straight and corner variants (including 2B, 2XL, etc.) - max 6 modules
  if (modelId.match(/^dragon-(2|2-xl|2-slim|2b|2b-xl|2b-slim)$/)) {
    maxModules = 6;
    maxNote = 'maksymalnie 6 sztuk';
  }
  // Dragon 8 variants - max 5 modules
  else if (modelId.match(/^dragon-(8|8-xxl|8-mini|8-slim|8b|8b-xxl)$/)) {
    maxModules = 5;
    maxNote = 'maksymalnie 5 sztuk';
  }
  // Dragon 4 SLIM PORTAL variants - max 5 modules
  else if (modelId.match(/^dragon-4-slim.*-portal$/)) {
    maxModules = 5;
    maxNote = 'maksymalnie 5 sztuk';
  }
  
  return {
    id: 'thermalModules',
    title: 'MODUŁY TERMOAKUMULACYJNE',
    selectedValue: 'none',
    options: [
      {
        value: 'thermal',
        label: moduleLabel,
        description: `Specjalne moduły termoakumulacyjne (${maxNote})`,
        isCheckbox: true,
        hasCounter: true,
        price: singleModulePrice,
        counterMin: 1,
        counterMax: maxModules,
        counterDefault: 1,
        disabled: akuBoxSelected // Disable when AKU BOX is selected
      }
    ]
  };
}

// Thermal hangers configuration (zawiesia termoakumulacyjne)
const THERMAL_HANGERS_CONFIG: Record<string, { weight: number, price: number }> = {
  // Dragon 2, 2XL, 2 SLIM
  'dragon-2': { weight: 60, price: 740 },
  'dragon-2-xl': { weight: 60, price: 740 },
  'dragon-2-slim': { weight: 60, price: 740 },
  // Dragon 2 PORTAL
  'dragon-2-portal': { weight: 60, price: 740 },
  'dragon-2-xl-portal': { weight: 60, price: 740 },
  // Dragon 2B, 2B XL, 2B SLIM  
  'dragon-2b': { weight: 45, price: 560 },
  'dragon-2b-xl': { weight: 45, price: 560 },
  'dragon-2b-slim': { weight: 45, price: 560 },
  // Dragon 4, 4 XL, 4 SLIM
  'dragon-4': { weight: 72, price: 885 },
  'dragon-4-xl': { weight: 57, price: 700 },
  'dragon-4-slim': { weight: 72, price: 885 },
  // Dragon 4 PORTAL
  'dragon-4-portal': { weight: 72, price: 885 },
  'dragon-4-xl-portal': { weight: 72, price: 885 },
  'dragon-4-slim-portal': { weight: 72, price: 885 },
  'dragon-4-slim-xl-portal': { weight: 72, price: 885 },
  // Dragon 4B, 4B XL, 4B SLIM
  'dragon-4b': { weight: 57, price: 700 },
  'dragon-4b-xl': { weight: 57, price: 700 },
  'dragon-4b-slim': { weight: 57, price: 700 },
  'dragon-4b-xl-slim': { weight: 57, price: 700 },
  // Dragon 6, 6 XL, 6 XXL
  'dragon-6': { weight: 72, price: 885 },
  'dragon-6-xl': { weight: 72, price: 885 },
  'dragon-6-xxl': { weight: 72, price: 885 },
  // Dragon 6 PORTAL
  'dragon-6-xl-portal': { weight: 72, price: 885 },
  'dragon-6-xxl-portal': { weight: 72, price: 885 },
  // Dragon 8, 8 XXL
  'dragon-8': { weight: 45, price: 560 },
  'dragon-8-xxl': { weight: 45, price: 560 },
  // Dragon 8B, 8B XXL
  'dragon-8b': { weight: 30, price: 390 },
  'dragon-8b-xxl': { weight: 30, price: 390 },
  // Dragon 9, 9XL
  'dragon-9': { weight: 75, price: 920 },
  'dragon-9-xl': { weight: 75, price: 920 }
};

// Check if thermal hangers are available for the model
export function isThermalHangersAvailable(model: FireplaceModel | null): boolean {
  if (!model || !model.id) return false;
  return model.id in THERMAL_HANGERS_CONFIG;
}

// Get thermal hangers data for the model
export function getThermalHangersData(model: FireplaceModel | null): { weight: number, price: number } | null {
  if (!model || !model.id) return null;
  return THERMAL_HANGERS_CONFIG[model.id] || null;
}

// Generate thermal hangers options
export function generateThermalHangersOptions(model: FireplaceModel | null): OptionGroup | null {
  if (!isThermalHangersAvailable(model)) return null;
  
  const hangersData = getThermalHangersData(model);
  if (!hangersData) return null;
  
  return {
    id: 'thermalHangers',
    title: 'ZAWIESIA TERMOAKUMULACYJNE',
    selectedValue: 'none',
    options: [
      {
        value: 'yes',
        label: 'Komplet z uchwytami',
        description: `Ciężar: ${hangersData.weight} kg`,
        isCheckbox: true,
        price: hangersData.price
      }
    ]
  };
}

// Generate controllers options (STEROWNIKI)
export function generateControllersOptions(model: FireplaceModel | null, config?: Configuration): OptionGroup | null {
  if (!model?.extraPrices?.controllers || !model?.labels?.controllers) return null;
  
  const controllerPrices = model.extraPrices.controllers;
  const controllerLabels = model.labels.controllers;
  
  // Sprawdź czy to model NEMO
  const isNemo = model.id?.startsWith('nemo-');
  
  // Kontrolery wymagające wyboru koloru (Dragon i NEMO)
  const controllersWithColor = isNemo 
    ? [] // NEMO używa funkcji nemoRequiresColorSelection
    : ['rt-08-om-grafik-wb', 'rt-08-om-grafik-duo-integra-wb'];
  
  const options: OptionItem[] = [];
  
  // Dodaj wszystkie opcje sterowników oprócz 'none' i 'czujnik-drzwiczek'
  Object.keys(controllerPrices).forEach(key => {
    if (key === 'none' || key === 'czujnik-drzwiczek') return; // Pomijamy 'none' i czujnik
    
    const price = controllerPrices[key];
    const label = controllerLabels[key] || key;
    
    // Wszystkie sterowniki nie zawierają czujnika drzwiczek
    const description = 'Sterownik nie zawiera czujnika drzwiczek';
    
    // Sprawdź czy kontroler wymaga wyboru koloru
    const requiresColor = isNemo 
      ? nemoRequiresColorSelection(key)
      : controllersWithColor.includes(key);
    
    options.push({
      value: key,
      label: label,
      price: price,
      description: description,
      isCheckbox: true, // Checkbox - można odznaczyć
      // Oznaczamy które kontrolery wymagają wyboru koloru
      ...(requiresColor && { colorPreview: 'white-black' })
    });
  });
  
  // Dodaj czujnik drzwiczek jako osobny checkbox na końcu
  if (controllerPrices['czujnik-drzwiczek'] !== undefined) {
    const currentController = config?.controller || 'none';
    // Czujnik dostępny bez sterownika, ale dla NEMO tylko z RT-08GX
    const isDoorSensorDisabled = currentController !== 'none' && isNemo && !nemoIsRT08GX(currentController);
    
    options.push({
      value: 'czujnik-drzwiczek',
      label: controllerLabels['czujnik-drzwiczek'] || 'Czujnik drzwiczek',
      price: controllerPrices['czujnik-drzwiczek'],
      description: isDoorSensorDisabled 
        ? 'Opcja niedostępna - pasuje tylko do modeli RT-08GX'
        : 'Czujnik monitorujący stan drzwiczek kominka',
      isCheckbox: true,
      disabled: isDoorSensorDisabled
    });
  }
  
  if (options.length === 0) return null;
  
  return {
    id: 'controllers',
    title: 'STEROWNIKI',
    selectedValue: 'none',
    options: options
  };
}

// Generate FUSION options if available
export function generateFusionOptions(model: FireplaceModel | null, config: Configuration): OptionGroup | null {
  const variant = config.variant === 'lift' ? 'lift' : 'standard';
  const hasFusion = isFusionAvailable(model);
  const hasFusionCorner = isFusionCornerAvailable(model, variant);
  
  if (!hasFusion && !hasFusionCorner) return null;
  
  const options: OptionItem[] = [];
  
  // FUSION (standardowy - 5 modułów, 3 ściany)
  if (hasFusion) {
    const isFusionSelected = config.fusionBasic === 'fusion';
    const fusionPrices = getFusionPrices(model?.id || 'default', variant);
    const fusionDimensions = getFusionDimensions(model, variant);
    
    options.push({
      value: 'fusion',
      label: 'FUSION (5 modułów, 3 ściany)',
      description: `Standardowe moduły FUSION, Wymiary (sz/w/g [mm]): ${fusionDimensions}`,
      isCheckbox: true,
      price: fusionPrices.fusion,
      exclusiveWith: 'fusionCorner' // Wykluczają się wzajemnie
    });
    
    options.push({
      value: 'fusionModules',
      label: 'Dodatkowy moduł FUSION',
      description: 'Ilość dodatkowych modułów FUSION (wys. 330 mm) - maksymalnie 2 sztuki',
      isCheckbox: true,
      disabled: !isFusionSelected,
      hasCounter: true,
      price: fusionPrices.modules,
      counterMin: 1,
      counterMax: 2,
      counterDefault: 1
    });
  }
  
  // FUSION CORNER (5 modułów, 2 ściany)
  if (hasFusionCorner) {
    const isFusionCornerSelected = config.fusionCorner === 'fusion-corner';
    const fusionCornerPrices = getFusionCornerPrices(model?.id || 'default', variant);
    const fusionCornerDimensions = getFusionCornerDimensions(model, variant);
    
    if (fusionCornerPrices) {
      options.push({
        value: 'fusionCorner',
        label: 'FUSION CORNER (5 modułów, 2 ściany)',
        description: `Standardowe moduły FUSION CORNER, Wymiary (sz/w/g [mm]): ${fusionCornerDimensions}`,
        isCheckbox: true,
        price: fusionCornerPrices.fusion,
        exclusiveWith: 'fusion' // Wykluczają się wzajemnie
      });
      
      options.push({
        value: 'fusionCornerModules',
        label: 'Dodatkowy moduł FUSION CORNER',
        description: 'Ilość dodatkowych modułów FUSION CORNER (wys. 330 mm) - maksymalnie 2 sztuki',
        isCheckbox: true,
        disabled: !isFusionCornerSelected,
        hasCounter: true,
        price: fusionCornerPrices.modules,
        counterMin: 1,
        counterMax: 2,
        counterDefault: 1
      });
    }
  }
  
  if (options.length === 0) return null;
  
  return {
    id: 'fusion',
    title: 'ZABUDOWY BETONOWE UNICO FUSION',
    selectedValue: 'none',
    options
  };
}

// Generate front variant options for vis-a-vis models
export function generateFrontVariantOptions(model: FireplaceModel | null): OptionGroup {
  const options: OptionItem[] = [];
  const hasStandard = !!model?.basePrices?.standard;
  const hasLift = !!model?.basePrices?.lift && isLiftAvailable(model);
  const liftDisabled = isAkuVisAVis(model) ? true : !hasLift;

  if (hasStandard) {
    options.push({
      value: 'standard',
      label: 'Standard - otwieranie na bok',
      description: 'Standardowy system otwierania'
    });
  }

  if (hasLift) {
    options.push({
      value: 'lift',
      label: 'LIFT (gilotyna) - podnoszenie do góry',
  description: liftDisabled ? 'Niedostępny dla tego modelu' : 'System podnoszenia drzwiczek do góry',
      disabled: liftDisabled
    });
  } else if (isAkuVisAVis(model)) {
    // show lift option as disabled for AKU vis-a-vis models
    options.push({
      value: 'lift',
  label: 'LIFT (gilotyna) - podnoszenie do góry',
  description: 'Niedostępny dla tego modelu',
      disabled: true
    });
  }

  return {
    id: 'front-variant',
    title: 'WARIANT PRZODU',
    options,
    visible: true,
    selectedValue: hasStandard ? 'standard' : (hasLift ? 'lift' : '')
  };
}

// Generate back variant options for vis-a-vis models
export function generateBackVariantOptions(model: FireplaceModel | null, config: Configuration): OptionGroup {
  const options: OptionItem[] = [];
  const frontVariant = config['front-variant'];
  const isFrontLift = frontVariant === 'lift';
  const hasStandard = !!model?.basePrices?.standard;
  const hasLift = !!model?.basePrices?.lift && isLiftAvailable(model);
  const liftDisabled = isAkuVisAVis(model) ? true : !hasLift || isFrontLift;

  if (hasStandard) {
    options.push({
      value: 'standard',
      label: 'Standard - otwieranie na bok',
      description: 'Standardowy system otwierania'
    });
  }

  if (hasLift) {
    options.push({
      value: 'lift',
      label: 'LIFT (gilotyna) - podnoszenie do góry',
  description: liftDisabled ? 'Niedostępny dla tego modelu' : (isFrontLift ? 'System podnoszenia drzwiczek do góry (niedostępny w konfiguratorze gdy przód ma LIFT (gilotyna))' : 'System podnoszenia drzwiczek do góry'),
      disabled: liftDisabled
    });
  } else if (isAkuVisAVis(model)) {
    // show lift option as disabled for AKU vis-a-vis models
    options.push({
      value: 'lift',
  label: 'LIFT (gilotyna) - podnoszenie do góry',
  description: 'Niedostępny dla tego modelu',
      disabled: true
    });
  }

  return {
    id: 'back-variant',
    title: 'WARIANT TYŁU',
    options,
    visible: true,
    selectedValue: hasStandard ? 'standard' : (hasLift ? 'lift' : '')
  };
}

// Generate front handle side options for vis-a-vis models
export function generateFrontHandleSideOptions(config: Configuration): OptionGroup {
  const frontVariant = config['front-variant'];
  const isStandardVariant = frontVariant === 'standard';
  const isLiftVariant = frontVariant === 'lift';
  const isVisible = isStandardVariant; // Only visible for standard variants, not for LIFT
  
  const title = isLiftVariant ? 'STRONA KLAMKI SERWISOWEJ (PRZÓD)' : 'STRONA KLAMKI (PRZÓD)';
  const leftLabel = isLiftVariant ? 'Klamka serwisowa po stronie lewej' : 'Klamka po stronie lewej';
  const rightLabel = isLiftVariant ? 'Klamka serwisowa po stronie prawej' : 'Klamka po stronie prawej';
  
  return {
    id: 'front-handleSide',
    title: title,
    options: [
      { value: 'left', label: 'Lewa', description: leftLabel },
      { value: 'right', label: 'Prawa', description: rightLabel }
    ],
    visible: isVisible
  };
}

// Generate back handle side options for vis-a-vis models
export function generateBackHandleSideOptions(config: Configuration): OptionGroup {
  const backVariant = config['back-variant'];
  const isStandardVariant = backVariant === 'standard';
  const isLiftVariant = backVariant === 'lift';
  const isVisible = isStandardVariant; // Only visible for standard variants, not for LIFT
  
  const title = isLiftVariant ? 'STRONA KLAMKI SERWISOWEJ (TYŁ)' : 'STRONA KLAMKI (TYŁ)';
  const leftLabel = isLiftVariant ? 'Klamka serwisowa po stronie lewej' : 'Klamka po stronie lewej';
  const rightLabel = isLiftVariant ? 'Klamka serwisowa po stronie prawej' : 'Klamka po stronie prawej';
  
  return {
    id: 'back-handleSide',
    title: title,
    options: [
      { value: 'left', label: 'Lewa', description: leftLabel },
      { value: 'right', label: 'Prawa', description: rightLabel }
    ],
    visible: isVisible
  };
}

// Generate front handle options for vis-a-vis models
export function generateFrontHandleOptions(config: Configuration): OptionGroup {
  const frontVariant = config['front-variant'];
  const isLift = frontVariant === 'lift';
  // Handle color is available for both standard and LIFT variants
  const isVisible = frontVariant === 'standard' || frontVariant === 'lift';
  
  const baseOptions = [
    { value: 'classic-black', label: 'Classic czarna', description: 'Standardowa klamka czarna' },
    { value: 'classic-silver', label: 'Classic srebrna', description: 'Standardowa klamka srebrna' }
  ];

  // Add design options only for standard variant
  if (!isLift) {
    baseOptions.push(
      { value: 'design-black', label: 'Design czarna', description: 'Designerska klamka czarna' },
      { value: 'design-black-silver', label: 'Design czarno-srebrna', description: 'Designerska klamka dwukolorowa' }
    );
  }

  return {
    id: 'front-handle',
    title: 'KOLOR KLAMKI (PRZÓD)',
    options: baseOptions,
    visible: isVisible
  };
}

// Generate back handle options for vis-a-vis models
export function generateBackHandleOptions(config: Configuration): OptionGroup {
  const backVariant = config['back-variant'];
  const isLift = backVariant === 'lift';
  // Handle color is available for both standard and LIFT variants
  const isVisible = backVariant === 'standard' || backVariant === 'lift';
  
  const baseOptions = [
    { value: 'classic-black', label: 'Classic czarna', description: 'Standardowa klamka czarna' },
    { value: 'classic-silver', label: 'Classic srebrna', description: 'Standardowa klamka srebrna' }
  ];

  // Add design options only for standard variant
  if (!isLift) {
    baseOptions.push(
      { value: 'design-black', label: 'Design czarna', description: 'Designerska klamka czarna' },
      { value: 'design-black-silver', label: 'Design czarno-srebrna', description: 'Designerska klamka dwukolorowa' }
    );
  }

  return {
    id: 'back-handle',
    title: 'KOLOR KLAMKI (TYŁ)',
    options: baseOptions,
    visible: isVisible
  };
}

// Generate front bezel options for vis-a-vis models
export function generateFrontBezelOptions(config: Configuration, model: FireplaceModel | null): OptionGroup {
  const frontVariant = config['front-variant'] || 'standard';
  const options: OptionItem[] = [];
  
  // Helper function to get description for bezel option
  const getBezelDescription = (key: string, isLift: boolean): string => {
    const descriptions: { [key: string]: string } = {
      'none': 'Standardowe wykończenie',
      'flush-15': 'Bez wywinięcia',
      'flush-30': isLift ? 'Wywinięcie 9mm' : 'Bez wywinięcia',
      'recessed-15': 'Bez wywinięcia',
      'recessed-30': 'Wywinięcie 9mm',
      'recessed-15-45': 'Kąt 45°',
      'recessed-lift-15': 'Bez wywinięcia',
      'recessed-lift-30': 'Wywinięcie 9mm',
      'recessed-lift-5': 'Bez wywinięcia'
    };
    return descriptions[key] || 'Wykończenie specjalne';
  };
  
  // Standard bezel options
  if (frontVariant === 'standard' && model?.extraPrices?.bezel?.standard) {
    Object.entries(model.extraPrices.bezel.standard).forEach(([key, price]) => {
      const label = model.labels?.bezel?.[key] || key;
      const numericPrice = typeof price === 'number' ? price : 0;
      options.push({
        value: key,
        label,
        description: getBezelDescription(key, false),
        ...(numericPrice > 0 ? { price: numericPrice } : {})
      });
    });
  }
  
  // Lift bezel options
  if (frontVariant === 'lift' && model?.extraPrices?.bezel?.lift) {
    Object.entries(model.extraPrices.bezel.lift).forEach(([key, price]) => {
      const label = model.labels?.bezel?.[key] || key;
      const numericPrice = typeof price === 'number' ? price : 0;
      options.push({
        value: key,
        label,
        description: getBezelDescription(key, true),
        ...(numericPrice > 0 ? { price: numericPrice } : {})
      });
    });
  }
  
  return {
    id: 'front-bezel',
    title: 'BLENDA (PRZÓD)',
    options,
    visible: true
  };
}

// Generate back bezel options for vis-a-vis models
export function generateBackBezelOptions(config: Configuration, model: FireplaceModel | null): OptionGroup {
  const backVariant = config['back-variant'] || 'standard';
  const options: OptionItem[] = [];
  
  // Helper function to get description for bezel option
  const getBezelDescription = (key: string, isLift: boolean): string => {
    const descriptions: { [key: string]: string } = {
      'none': 'Standardowe wykończenie',
      'flush-15': 'Bez wywinięcia',
      'flush-30': isLift ? 'Wywinięcie 9mm' : 'Bez wywinięcia',
      'recessed-15': 'Bez wywinięcia',
      'recessed-30': 'Wywinięcie 9mm',
      'recessed-15-45': 'Kąt 45°',
      'recessed-lift-15': 'Bez wywinięcia',
      'recessed-lift-30': 'Wywinięcie 9mm',
      'recessed-lift-5': 'Bez wywinięcia'
    };
    return descriptions[key] || 'Wykończenie specjalne';
  };
  
  // Standard bezel options
  if (backVariant === 'standard' && model?.extraPrices?.bezel?.standard) {
    Object.entries(model.extraPrices.bezel.standard).forEach(([key, price]) => {
      const label = model.labels?.bezel?.[key] || key;
      const numericPrice = typeof price === 'number' ? price : 0;
      options.push({
        value: key,
        label,
        description: getBezelDescription(key, false),
        ...(numericPrice > 0 ? { price: numericPrice } : {})
      });
    });
  }
  
  // Lift bezel options
  if (backVariant === 'lift' && model?.extraPrices?.bezel?.lift) {
    Object.entries(model.extraPrices.bezel.lift).forEach(([key, price]) => {
      const label = model.labels?.bezel?.[key] || key;
      const numericPrice = typeof price === 'number' ? price : 0;
      options.push({
        value: key,
        label,
        description: getBezelDescription(key, true),
        ...(numericPrice > 0 ? { price: numericPrice } : {})
      });
    });
  }
  
  return {
    id: 'back-bezel',
    title: 'BLENDA (TYŁ)',
    options,
    visible: true
  };
}

// Generate all option groups for a model and configuration
export function generateAllOptions(model: FireplaceModel | null, config: Configuration, updateConfig?: (key: keyof Configuration, value: any) => void): OptionGroup[] {
  const options: OptionGroup[] = [];

  if (!model) return options;

  // Check if this is a vis-a-vis model
  if (isVisAVisModel(model)) {
    // Add NEMO thermal protection first if it's a NEMO model
    const nemoThermalOptions = generateNemoThermalProtectionOptions(model);
    if (nemoThermalOptions) {
      options.push(nemoThermalOptions);
    }
    
    // Add AKU+ first if available for vis-a-vis AKU models
    const akuPlusOptions = generateAkuPlusOptions(model);
    if (akuPlusOptions) {
      options.push(akuPlusOptions);
    }
    // Auto-set 'not-applicable' values for LIFT variants to ensure validation passes
    // LIFT has handle color but no handle side selection
    const currentFrontVariant = config['front-variant'];
    const currentBackVariant = config['back-variant'];
    
    // Auto-set front handleSide for LIFT variant (but keep handle for color selection)
    if (currentFrontVariant === 'lift' && config['front-handleSide'] !== 'not-applicable') {
      if (updateConfig) {
        updateConfig('front-handleSide', 'not-applicable');
      } else {
        config['front-handleSide'] = 'not-applicable';
      }
    }
    
    // Auto-set back handleSide for LIFT variant (but keep handle for color selection)
    if (currentBackVariant === 'lift' && config['back-handleSide'] !== 'not-applicable') {
      if (updateConfig) {
        updateConfig('back-handleSide', 'not-applicable');
      } else {
        config['back-handleSide'] = 'not-applicable';
      }
    }
    
    // VIS-A-VIS MODEL: Sequential unlocking - first all front options, then all back options
    
    // Ensure defaults: auto-select 'standard' for front/back variants when applicable
    if (isAkuVisAVis(model)) {
      if (!config['front-variant'] || config['front-variant'] === '') {
        if (updateConfig) updateConfig('front-variant', 'standard'); else config['front-variant'] = 'standard';
      }
      if (!config['back-variant'] || config['back-variant'] === '') {
        if (updateConfig) updateConfig('back-variant', 'standard'); else config['back-variant'] = 'standard';
      }
    }

    // FRONT SECTION
    
    // Check if NEMO thermal protection is selected (if required)
    const isNemoThermalSelected = !isNemoModel(model?.id || null) || !!config.nemoThermalProtection;
    
    // 1. Front variant (always visible, but disabled for NEMO until thermal protection is selected)
    const frontVariantOptions = generateFrontVariantOptions(model);
    if (isNemoModel(model?.id || null)) {
      frontVariantOptions.disabled = !isNemoThermalSelected;
    }
    options.push(frontVariantOptions);
    
  const frontVariant = config['front-variant'];
    const isFrontVariantSelected = frontVariant && frontVariant !== '';
    
    // 2. Front handle side (enabled when front variant selected AND variant is standard)
    const frontHandleSideOptions = generateFrontHandleSideOptions(config);
    frontHandleSideOptions.disabled = !isFrontVariantSelected;
    options.push(frontHandleSideOptions);
    
    const frontHandleSide = config['front-handleSide'];
    // For LIFT variant, skip handleSide requirement
    const isFrontHandleSideSelected = isFrontVariantSelected && (
      frontVariant === 'lift' || !!frontHandleSide
    );
    
    // 3. Front handle (enabled when front handle side selected OR front variant is LIFT)
    const frontHandleOptions = generateFrontHandleOptions(config);
    frontHandleOptions.disabled = !isFrontHandleSideSelected;
    options.push(frontHandleOptions);
    
    const frontHandle = config['front-handle'];
    // For LIFT variant, handle is required but handleSide is not
    const isFrontHandleSelected = isFrontHandleSideSelected && (
      (frontVariant === 'lift' && frontHandle && frontHandle !== '') ||
      (frontVariant === 'standard' && frontHandle && frontHandle !== '')
    );
    

    
    // 4. Front bezel (enabled when front handle selected AND service handle side selected for LIFT)
    const frontBezelOptions = generateFrontBezelOptions(config, model);
    const frontBezelEnabled = isFrontHandleSelected;
    frontBezelOptions.disabled = !frontBezelEnabled;
    options.push(frontBezelOptions);
    
    const frontBezel = config['front-bezel'];
    const isFrontBezelSelected = frontBezelEnabled && frontBezel && frontBezel !== '';
    
    // BACK SECTION
    // 5. Back variant (enabled when front bezel selected)
    const backVariantOptions = generateBackVariantOptions(model, config);
    backVariantOptions.disabled = !isFrontBezelSelected;
    options.push(backVariantOptions);
    
    const backVariant = config['back-variant'];
    const isBackVariantSelected = isFrontBezelSelected && backVariant && backVariant !== '';
    
    // 6. Back handle side (enabled when back variant selected AND variant is standard)
    const backHandleSideOptions = generateBackHandleSideOptions(config);
    backHandleSideOptions.disabled = !isBackVariantSelected;
    options.push(backHandleSideOptions);
    
    const backHandleSide = config['back-handleSide'];
    // For LIFT variant, skip handleSide requirement
    const isBackHandleSideSelected = isBackVariantSelected && (
      backVariant === 'lift' || !!backHandleSide
    );
    
    // 7. Back handle (enabled when back handle side selected OR back variant is LIFT)
    const backHandleOptions = generateBackHandleOptions(config);
    backHandleOptions.disabled = !isBackHandleSideSelected;
    options.push(backHandleOptions);
    
    const backHandle = config['back-handle'];
    // For LIFT variant, handle is required but handleSide is not
    const isBackHandleSelected = isBackHandleSideSelected && (
      (backVariant === 'lift' && backHandle && backHandle !== '') ||
      (backVariant === 'standard' && backHandle && backHandle !== '')
    );
    

    
    // 8. Back bezel (enabled when back handle selected AND service handle side selected for LIFT)
    const backBezelOptions = generateBackBezelOptions(config, model);
    const backBezelEnabled = isBackHandleSelected;
    backBezelOptions.disabled = !backBezelEnabled;
    options.push(backBezelOptions);
    
    const backBezel = config['back-bezel'];
    const isBackBezelSelected = backBezelEnabled && backBezel && backBezel !== '';
    
    // FINAL SECTION
    // 9. Glass options (enabled when both bezels selected)
    const glassOptions = generateGlassOptions(model, config);
    glassOptions.disabled = !isBackBezelSelected;
    options.push(glassOptions);
    
    const glass = config.glass;
    const isGlassSelected = isBackBezelSelected && glass && glass !== '';
    
    // 10. Ceramic options (enabled when glass selected)
    const ceramicOptions = generateCeramicOptions();
    ceramicOptions.disabled = !isGlassSelected;
    options.push(ceramicOptions);
    
    const ceramic = config.ceramic;
    const isCeramicSelected = isGlassSelected && ceramic && ceramic !== '';
    
    // 11. Fusion options (enabled when ceramic selected)
    const fusionOptions = generateFusionOptions(model, config);
    if (fusionOptions) {
      fusionOptions.disabled = !isCeramicSelected;
      options.push(fusionOptions);
    }

    // 12. Accumulation materials options (enabled when ceramic selected)
    const accumulationMaterialsOptions = generateAccumulationMaterialsOptions(model, config);
    if (accumulationMaterialsOptions) {
      accumulationMaterialsOptions.disabled = !isCeramicSelected;
      options.push(accumulationMaterialsOptions);
    }

    // 13. Thermal modules options (enabled when ceramic selected)  
    const thermalModulesOptions = generateThermalModulesOptions(model, config);
    if (thermalModulesOptions) {
      thermalModulesOptions.disabled = !isCeramicSelected;
      options.push(thermalModulesOptions);
    }
    
    // 14. Thermal hangers options (enabled when ceramic selected)
    const thermalHangersOptions = generateThermalHangersOptions(model);
    if (thermalHangersOptions) {
      thermalHangersOptions.disabled = !isCeramicSelected;
      options.push(thermalHangersOptions);
    }
    
    // 15. Controllers options (enabled when ceramic selected)
    const controllersOptions = generateControllersOptions(model, config);
    if (controllersOptions) {
      controllersOptions.disabled = !isCeramicSelected;
      options.push(controllersOptions);
    }
    
    // Service handle side is now handled locally after each handle selection
    
    return options;
  }

  // STANDARD MODEL: Original sequential unlocking
  
  // Add NEMO thermal protection first if it's a NEMO model
  const nemoThermalOptions = generateNemoThermalProtectionOptions(model);
  if (nemoThermalOptions) {
    options.push(nemoThermalOptions);
  }
  
  // Add AKU+ first if available
  const akuPlusOptions = generateAkuPlusOptions(model);
  if (akuPlusOptions) {
    options.push(akuPlusOptions);
  }

  // Sequential unlocking - each section is enabled only when previous is selected
  
  // Check if NEMO thermal protection is selected (if required)
  const isNemoThermalSelected = !isNemoModel(model?.id || null) || !!config.nemoThermalProtection;
  
  const isVariantSelected = config.variant && config.variant !== '';
  const isGlassSelected = config.glass && config.glass !== '';
  
  // Check if side glass is required and selected (only for corner LIFT Dragon/Nemo models)
  const sideGlassPrecheck = generateSideGlassOptions(config, model);
  const isSideGlassRequired = sideGlassPrecheck.visible;
  const isSideGlassSelected = !isSideGlassRequired || !!config.sideGlass;
  
  const isCeramicSelected = config.ceramic && config.ceramic !== '';
  const isHandleSelected = config.handle && config.handle !== '';
  const standardHandleSideVisible = generateHandleSideOptions(config, model).visible;
  
  // For standard corner models with side glass selection, handle side is automatically determined
  const isCornerModel = model?.glassType === 'narozna';
  const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
  const isStandardCornerWithSideGlass = config.variant === 'standard' && isCornerModel && isDragonOrNemo && !!config.sideGlass;
  
  const isHandleSideSelected = (config.variant === 'lift') || // lift nie wymaga handle side selection
                               isStandardCornerWithSideGlass || // standard corner with side glass auto-determines handle side
                               (!standardHandleSideVisible) || 
                               (standardHandleSideVisible && !!config.handleSide);

  // 1. Variant - always available (but disabled for NEMO until thermal protection is selected)
  const variantOptions = generateVariantOptions(model);
  if (isNemoModel(model?.id || null)) {
    variantOptions.disabled = !isNemoThermalSelected;
  }
  options.push(variantOptions);
  
  // 2. Glass - enabled when variant is selected
  const glassOptions = generateGlassOptions(model, config);
  glassOptions.disabled = !isVariantSelected;
  options.push(glassOptions);
  
  // 2b. Side glass - enabled when glass is selected (for corner Dragon/Nemo models)
  const sideGlassOptions = generateSideGlassOptions(config, model);
  if (sideGlassOptions.visible) {
    sideGlassOptions.disabled = !isVariantSelected || !isGlassSelected;
    options.push(sideGlassOptions);
  }
  
  // 3. Ceramic - enabled when glass and side glass (if required) are selected
  const ceramicOptions = generateCeramicOptions();
  ceramicOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected;
  options.push(ceramicOptions);
  
  // 4. Handle color - enabled when ceramic is selected
  const handleOptions = generateHandleOptions(config);
  handleOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected;
  options.push(handleOptions);
  

  
  // 6. Handle side - enabled when handle color is selected (only for standard variant)
  const handleSideOptions = generateHandleSideOptions(config, model);
  handleSideOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected;
  options.push(handleSideOptions);
  
  // 7. Bezel - enabled when handle side is selected (or not applicable)
  const bezelOptions = generateBezelOptions(config, model);
  bezelOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected || !isHandleSideSelected;
  options.push(bezelOptions);

  // 8. FUSION - enabled when bezel is selected
  const fusionOptions = generateFusionOptions(model, config);
  if (fusionOptions) {
    const isBezelSelected = !!config.bezel; // "none" is also a valid selection
    fusionOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected || !isHandleSideSelected || !isBezelSelected;
    options.push(fusionOptions);
  }

  // 9. Accumulation materials - enabled when bezel is selected
  const accumulationMaterialsOptions = generateAccumulationMaterialsOptions(model, config);
  if (accumulationMaterialsOptions) {
    const isBezelSelected = !!config.bezel; // "none" is also a valid selection
    accumulationMaterialsOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected || !isHandleSideSelected || !isBezelSelected;
    options.push(accumulationMaterialsOptions);
  }

  // 9. Thermal modules - enabled when bezel is selected
  const thermalModulesOptions = generateThermalModulesOptions(model, config);
  if (thermalModulesOptions) {
    const isBezelSelected = !!config.bezel; // "none" is also a valid selection
    thermalModulesOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected || !isHandleSideSelected || !isBezelSelected;
    options.push(thermalModulesOptions);
  }

  // 10. Thermal hangers - enabled when bezel is selected
  const thermalHangersOptions = generateThermalHangersOptions(model);
  if (thermalHangersOptions) {
    const isBezelSelected = !!config.bezel; // "none" is also a valid selection
    thermalHangersOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected || !isHandleSideSelected || !isBezelSelected;
    options.push(thermalHangersOptions);
  }

  // 11. Controllers (STEROWNIKI) - enabled when bezel is selected
  const controllersOptions = generateControllersOptions(model, config);
  if (controllersOptions) {
    const isBezelSelected = !!config.bezel; // "none" is also a valid selection
    controllersOptions.disabled = !isVariantSelected || !isGlassSelected || !isSideGlassSelected || !isCeramicSelected || !isHandleSelected || !isHandleSideSelected || !isBezelSelected;
    options.push(controllersOptions);
  }

  return options;
}
